import type {
  ActivityId,
  DisciplineId,
  FloorId,
  MaterialId,
  ProjectId,
  ScheduleEntryId,
  TowerId,
  UnitOfMeasureId,
} from '../shared/ids';
import { compareMaterials, type Material } from '../schedule/material';
import type { ScheduleEntry } from '../schedule/schedule-entry';
import type { UnitOfMeasure } from '../schedule/unit-of-measure';
import { compareTowers, type Tower } from './tower';
import { compareFloors, type Floor } from './floor';
import { compareDisciplines, type Discipline } from './discipline';
import type { Activity, ActivityStatus } from './activity';

/**
 * Montagem da árvore da obra.
 *
 * Função **pura**: recebe as listas planas já carregadas e devolve a hierarquia com os
 * contadores. Quem lê do banco é o repositório, em consultas por índice — a montagem não
 * conhece persistência e por isso é testável sem banco nenhum.
 *
 * ```text
 * OBRA → TORRE → PAVIMENTO → DISCIPLINA(ATIVIDADE) → ITEM
 * ```
 *
 * O pavimento aparece **uma única vez**, com suas disciplinas abaixo — é a normalização
 * exigida pelo requisito, materializada na interface.
 */

export interface NodeSummary {
  readonly floors: number;
  readonly disciplines: number;
  readonly activities: number;
  /** Itens de cronograma abaixo do nó. */
  readonly items: number;
  readonly byStatus: Readonly<Record<ActivityStatus, number>>;
}

/**
 * Item — o último nível da árvore.
 *
 * Traz o material e a unidade já resolvidos: a tela mostra "Cimento CP-II 50kg · 120 sc",
 * não dois identificadores. Material ou unidade ausentes do catálogo tornam o item órfão,
 * que é registrado e omitido em vez de derrubar a árvore inteira (§2.3).
 */
export interface ItemNode {
  readonly entry: ScheduleEntry;
  readonly material: Material;
  readonly unit: UnitOfMeasure;
}

export interface ActivityNode {
  readonly activity: Activity;
  readonly discipline: Discipline;
  readonly items: readonly ItemNode[];
  readonly itemCount: number;
}

export interface FloorNode {
  readonly floor: Floor;
  readonly activities: readonly ActivityNode[];
  readonly summary: NodeSummary;
}

export interface TowerNode {
  readonly tower: Tower;
  readonly floors: readonly FloorNode[];
  readonly summary: NodeSummary;
}

export interface StructureTree {
  readonly projectId: ProjectId;
  readonly towers: readonly TowerNode[];
  readonly summary: NodeSummary;
  /** Atividades cuja disciplina não foi encontrada — degradação elegante (§2.3). */
  readonly orphanActivityIds: readonly ActivityId[];
  /** Itens cujo material ou unidade não foi encontrado — mesma degradação. */
  readonly orphanEntryIds: readonly ScheduleEntryId[];
}

export interface BuildStructureTreeInput {
  readonly projectId: ProjectId;
  readonly towers: readonly Tower[];
  readonly floors: readonly Floor[];
  readonly disciplines: readonly Discipline[];
  readonly activities: readonly Activity[];
  /** Itens do cronograma ativo da obra. Ausente = árvore sem o último nível. */
  readonly entries?: readonly ScheduleEntry[];
  readonly materials?: readonly Material[];
  readonly units?: readonly UnitOfMeasure[];
}

function emptyByStatus(): Record<ActivityStatus, number> {
  return { PLANNED: 0, IN_PROGRESS: 0, DONE: 0, CANCELLED: 0 };
}

function mergeByStatus(
  target: Record<ActivityStatus, number>,
  source: Readonly<Record<ActivityStatus, number>>,
): void {
  target.PLANNED += source.PLANNED;
  target.IN_PROGRESS += source.IN_PROGRESS;
  target.DONE += source.DONE;
  target.CANCELLED += source.CANCELLED;
}

export function buildStructureTree(input: BuildStructureTreeInput): StructureTree {
  const disciplineById = new Map<DisciplineId, Discipline>(
    input.disciplines.map((discipline) => [discipline.id, discipline]),
  );

  const activitiesByFloor = new Map<FloorId, Activity[]>();
  const orphanActivityIds: ActivityId[] = [];

  for (const activity of input.activities) {
    if (!disciplineById.has(activity.disciplineId)) {
      // Referência quebrada: registra e segue, em vez de estourar a tela inteira.
      orphanActivityIds.push(activity.id);
      continue;
    }
    const list = activitiesByFloor.get(activity.floorId);
    if (list === undefined) activitiesByFloor.set(activity.floorId, [activity]);
    else list.push(activity);
  }

  const floorsByTower = new Map<TowerId, Floor[]>();
  for (const floor of input.floors) {
    const list = floorsByTower.get(floor.towerId);
    if (list === undefined) floorsByTower.set(floor.towerId, [floor]);
    else list.push(floor);
  }

  // ── Itens ─────────────────────────────────────────────────────────────────────
  // Indexados por atividade uma única vez: montar a árvore continua custando um passo
  // por item, não uma varredura por atividade.
  const materialById = new Map<MaterialId, Material>(
    (input.materials ?? []).map((material) => [material.id, material]),
  );
  const unitById = new Map<UnitOfMeasureId, UnitOfMeasure>(
    (input.units ?? []).map((unit) => [unit.id, unit]),
  );

  const itemsByActivity = new Map<ActivityId, ItemNode[]>();
  const orphanEntryIds: ScheduleEntryId[] = [];

  for (const entry of input.entries ?? []) {
    const material = materialById.get(entry.materialId);
    const unit = unitById.get(entry.unitId);
    if (material === undefined || unit === undefined) {
      orphanEntryIds.push(entry.id);
      continue;
    }
    const node: ItemNode = { entry, material, unit };
    const list = itemsByActivity.get(entry.activityId);
    if (list === undefined) itemsByActivity.set(entry.activityId, [node]);
    else list.push(node);
  }

  // Data necessária primeiro: a árvore mostra o que a obra precisa antes.
  for (const list of itemsByActivity.values()) {
    list.sort(
      (a, b) =>
        a.entry.requiredDate.localeCompare(b.entry.requiredDate) ||
        compareMaterials(a.material, b.material),
    );
  }

  const projectByStatus = emptyByStatus();
  const projectDisciplines = new Set<DisciplineId>();
  let projectFloors = 0;
  let projectActivities = 0;
  let projectItems = 0;

  const towers = [...input.towers].sort(compareTowers).map((tower): TowerNode => {
    const towerByStatus = emptyByStatus();
    const towerDisciplines = new Set<DisciplineId>();
    let towerActivities = 0;
    let towerItems = 0;

    const floors = (floorsByTower.get(tower.id) ?? [])
      .sort(compareFloors)
      .map((floor): FloorNode => {
        const floorByStatus = emptyByStatus();
        let floorItems = 0;

        const activities = (activitiesByFloor.get(floor.id) ?? [])
          .map((activity): ActivityNode => {
            const discipline = disciplineById.get(activity.disciplineId)!;
            const items = itemsByActivity.get(activity.id) ?? [];
            floorByStatus[activity.status] += 1;
            floorItems += items.length;
            towerDisciplines.add(activity.disciplineId);
            projectDisciplines.add(activity.disciplineId);
            return { activity, discipline, items, itemCount: items.length };
          })
          .sort((a, b) => compareDisciplines(a.discipline, b.discipline));

        towerActivities += activities.length;
        towerItems += floorItems;
        mergeByStatus(towerByStatus, floorByStatus);

        return {
          floor,
          activities,
          summary: {
            floors: 0,
            disciplines: activities.length,
            activities: activities.length,
            items: floorItems,
            byStatus: floorByStatus,
          },
        };
      });

    projectFloors += floors.length;
    projectActivities += towerActivities;
    projectItems += towerItems;
    mergeByStatus(projectByStatus, towerByStatus);

    return {
      tower,
      floors,
      summary: {
        floors: floors.length,
        disciplines: towerDisciplines.size,
        activities: towerActivities,
        items: towerItems,
        byStatus: towerByStatus,
      },
    };
  });

  return {
    projectId: input.projectId,
    towers,
    orphanActivityIds,
    orphanEntryIds,
    summary: {
      floors: projectFloors,
      disciplines: projectDisciplines.size,
      activities: projectActivities,
      items: projectItems,
      byStatus: projectByStatus,
    },
  };
}

/** `"5 disciplinas · 50 itens"` — o resumo à direita de cada nó da árvore. */
export function describeSummary(summary: NodeSummary, level: 'tower' | 'floor'): string {
  const parts: string[] = [];

  if (level === 'tower') {
    parts.push(`${String(summary.floors)} ${summary.floors === 1 ? 'pavimento' : 'pavimentos'}`);
  }
  parts.push(
    `${String(summary.disciplines)} ${summary.disciplines === 1 ? 'disciplina' : 'disciplinas'}`,
  );
  if (summary.items > 0) {
    parts.push(`${String(summary.items)} ${summary.items === 1 ? 'item' : 'itens'}`);
  }

  return parts.join(' · ');
}
